/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useState, ChangeEvent } from 'react'
import { useHistory } from 'react-router-dom'
import { Grid, Select, MenuItem, Typography, makeStyles, Button, InputLabel } from '@material-ui/core'
import { I18n, Auth } from 'aws-amplify'
import { AlertProps } from '@material-ui/lab'

// Components
import { Input, PillButton } from 'components'

// Helpers
import { graphQLMutation } from 'graphql/helpers'
import { updateUserBase } from 'graphql/customMutations'
import { useAppState } from 'providers'

// Images
import profileBg from 'assets/userProfileBg.jpg'
import iconEditProfile from 'assets/icon-edit-profile.svg'
import { TemporaryAlert } from 'components/shared/alerts'

interface IProfileErrors {
  firstName: string
  lastName: string
  title: string
}

const initialProfileErrors: IProfileErrors = {
  firstName: '',
  lastName: '',
  title: ''
}

interface IUserProfileProps {
  internal?: boolean
  toggleMenuDrawer: () => void
}

interface TemporaryMessage {
  message: string
  severity: AlertProps['severity']
}

export const UserProfile: FC<IUserProfileProps> = ({ toggleMenuDrawer }) => {
  const classes = useStyles()
  const history = useHistory()
  const {
    appState: { user },
    setUser
  } = useAppState()

  const [loading, setLoading] = useState<boolean>(false)
  const [editModeState, setEditModeState] = useState<boolean>(false)
  const [profileErrors, setProfileErrors] = useState<IProfileErrors>(initialProfileErrors)
  const [temporaryMessage, setTemporaryMessage] = useState<TemporaryMessage>({
    message: '',
    severity: 'success'
  })

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      updateUserData()
    }
  }

  const handleTitleChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const { target } = e
    setUser({
      ...user,
      title: target.value as string
    })
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e
    setUser({
      ...user,
      [target.name]: target.value
    })
  }

  const updateUserData = async () => {
    try {
      setLoading(true)
      const updatedUser = await graphQLMutation(
        updateUserBase,
        {
          id: user?.id,
          firstName: user?.firstName,
          lastName: user?.lastName,
          title: user?.title
        },
        'updateUser'
      )
      setUser(updatedUser)
      setEditModeState(false)
      setLoading(false)
    } catch (e) {
      console.log(e)
      return
    }
  }

  const logout = () => {
    toggleMenuDrawer()

    Auth.signOut()
    history.push('/')
  }

  const profileDisplay = (
    <>
      <img src={profileBg} alt='profile background' />
      <section className={classes.profileMain}>
        <div className={classes.name}>
          {user?.firstName} {user?.lastName}
        </div>

        <Button
          startIcon={<img src={iconEditProfile} alt='Edit profile' width='18px' />}
          onClick={() => setEditModeState(true)}
        >
          <Typography component='span' variant='subtitle2'>
            Edit Profile
          </Typography>
        </Button>

        <PillButton
          className={classes.logoutButton}
          loading={loading}
          type='submit'
          backgroundColor='transparent'
          onClick={() => logout()}
        >
          Log out
        </PillButton>
      </section>
    </>
  )

  const editModeDisplay = (
    <section className={classes.profileMain}>
      <TemporaryAlert
        message={temporaryMessage.message}
        onClose={() => setTemporaryMessage({ message: '', severity: undefined })}
        severity={temporaryMessage.severity}
      />

      <Input
        label={I18n.get('firstName')}
        error={!!profileErrors.firstName}
        helperText={profileErrors.firstName}
        onFocus={() => setProfileErrors({ ...profileErrors, firstName: '' })}
        defaultValue={user?.firstName}
        type='text'
        name='firstName'
        required
        onChange={handleChange}
        onKeyPress={handleKeyPress}
      />

      <Input
        label={I18n.get('lastName')}
        error={!!profileErrors.lastName}
        helperText={profileErrors.lastName}
        onFocus={() => setProfileErrors({ ...profileErrors, lastName: '' })}
        defaultValue={user?.lastName}
        type='text'
        name='lastName'
        required
        onChange={handleChange}
        onKeyPress={handleKeyPress}
      />
      <InputLabel shrink required>
        {I18n.get('titlePosition')}
      </InputLabel>
      <Select
        labelId='personal-size-input'
        fullWidth
        name='title'
        value={user?.title}
        label={I18n.get('titlePosition')}
        onFocus={() => setProfileErrors({ ...profileErrors, title: '' })}
        onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleTitleChange(e)}
        inputProps={{ className: classes.titleSelect }}
      >
        <MenuItem value='c-suite'>{I18n.get('cSuite')}</MenuItem>
        <MenuItem value='vp'>{I18n.get('vp')}</MenuItem>
        <MenuItem value='director'>{I18n.get('director')}</MenuItem>
        <MenuItem value='manager'>{I18n.get('manager')}</MenuItem>
        <MenuItem value='staff'>{I18n.get('staff')}</MenuItem>
      </Select>

      <Grid item xs={12}>
        <PillButton
          className={classes.inlineButton}
          loading={loading}
          type='submit'
          onClick={() => setEditModeState(false)}
        >
          Close
        </PillButton>

        <PillButton
          className={classes.inlineButton}
          loading={loading}
          type='submit'
          disabled={!user?.lastName || !user.firstName}
          onClick={() => updateUserData()}
          solid
        >
          Save
        </PillButton>
      </Grid>
    </section>
  )

  return (
    <div className={classes.root}>
      {!editModeState && profileDisplay}
      {editModeState && editModeDisplay}
    </div>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: '-38px',
    width: '100%',
    height: '100%',
    maxWidth: 350,
    backgroundColor: theme.palette.background.paper,
    position: 'relative'
  },
  avatar: {
    marginLeft: 'calc(50% - 40px)',
    marginTop: '-50px',
    width: '76px',
    height: '76px',
    fontSize: '38px',
    position: 'absolute',
    backgroundColor: '#0088CE'
  },
  avatarEdit: {
    width: '100px',
    height: '100px',
    margin: '0',
    position: 'relative',
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      margin: '0 auto'
    }
  },
  name: {
    margin: '4px',
    fontSize: '24px',
    fontFamily: '"Verizon-Bold"',
    textAlign: 'center'
  },
  title: {
    margin: '6px 0 36px',
    fontSize: '14px',
    textAlign: 'center'
  },
  logoutButton: {
    minWidth: '165px',
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    margin: '0 auto',
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      bottom: '-30px'
    }
  },
  closeDrawer: {
    position: 'absolute',
    bottom: '30px',
    marginBottom: '50px'
  },
  dotSeperator: {
    position: 'relative',
    display: 'inline-block',
    top: '-4px',
    height: '4px',
    width: '4px',
    margin: '0px 12px',
    borderRadius: '50% 50%',
    backgroundColor: 'black'
  },
  profileMain: {
    color: 'black',
    height: 'calc(100% - 150px - 60px)',
    padding: '30px 16px'
  },
  input: {
    color: '#000',
    margin: '20px 0px',
    borderRadius: 0,
    '& .MuiInputBase-root': {
      backgroundColor: '#fff'
    },
    '& fieldset': {
      borderRadius: 0,
      borderColor: '#dadada',
      borderBottomColor: '#000'
    }
  },
  permissionButton: {
    margin: '8px 0px'
  },
  inlineButton: {
    fontFamily: 'Verizon-Regular',
    height: '26px',
    fontSize: '12px',
    padding: '6px 14px',
    margin: '20px 10px 0 0'
  },
  footer: {
    bottom: 0,
    padding: '8px',
    width: '100%',
    display: 'flex',
    position: 'fixed',
    backgroundColor: '#000000',
    justifyContent: 'flex-start'
  },
  arrowIcon: {
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      width: '10px'
    }
  },
  liveChatContainer: {
    height: 42,
    backgroundColor: '#F6F6F6',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 16,
    paddingRight: 16
  },
  liveChat: {
    flex: 1
  },
  switchColor: {
    color: 'red'
  },
  onlineIndicator: {
    position: 'absolute'
  },
  uploadContainer: {
    position: 'relative',
    [`${theme.breakpoints.down('sm')}, screen and (max-height: 540px)`]: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      textAlign: 'center'
    }
  },
  resetButton: {
    fontFamily: 'Verizon-Regular',
    fontSize: '12px',
    position: 'absolute',
    padding: '2px 10px',
    margin: 0,
    bottom: 6,
    right: 55
  },
  titleSelect: {
    padding: '11.7px 14px'
  }
}))
